import { useState, useEffect, useRef } from 'react'
import { Constants, useMeeting, usePubSub } from '@videosdk.live/react-sdk'
import { nameTruncated, trimSnackBarText } from '../utils/helper'
import ConfirmBox from '../components/ConfirmBox'
import { toast } from 'react-toastify'
import { useMeetingAppContext } from '../MeetingAppContextDef'
import Layout from '@/components/layout'
import MeetingRoom from './components/MeetingRoom'
import { useSocketContext } from '@/contexts/SocketProvider'
import { useHall } from '@/contexts/HallProvider'

type MeetingError = { code: string; message: string } | null

type Props = {
  onMeetingLeave: () => void
  setIsMeetingLeft: (v: boolean) => void
  isReady?: boolean
}

export function MeetingContainer({ onMeetingLeave, setIsMeetingLeft }: Props) {
  const { setSelectedMic, setSelectedWebcam, setSelectedSpeaker, useRaisedHandParticipants } =
    useMeetingAppContext()

  const socket = useSocketContext()
  const { hall } = useHall()

  const [containerHeight, setContainerHeight] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)

  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false)
  const [meetingError, setMeetingError] = useState<MeetingError>(null)

  const mMeetingRef = useRef<ReturnType<typeof useMeeting> | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // measure container
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return
      setContainerHeight(containerRef.current.offsetHeight)
      setContainerWidth(containerRef.current.offsetWidth)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // unload cleanup (leave hall + meeting)
  useEffect(() => {
    const onUnload = () => {
      try {
        if (hall?.hallId) socket?.emit('leave-hall', { hallId: hall.hallId })
      } catch {}
      try {
        mMeetingRef.current?.leave?.()
      } catch {}
    }
    window.addEventListener('beforeunload', onUnload)
    return () => {
      onUnload()
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [socket, hall?.hallId])

  const { participantRaisedHand } = useRaisedHandParticipants()

  const _handleMeetingLeft = () => {
    setIsMeetingLeft(true)
  }

  const _handleOnRecordingStateChanged = ({ status }: { status: string }) => {
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      toast(
        status === Constants.recordingEvents.RECORDING_STARTED
          ? 'Meeting recording is started'
          : 'Meeting recording is stopped.',
        {
          position: 'bottom-left',
          autoClose: 4000,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
        },
      )
    }
  }

  function onParticipantJoined(participant: any) {
    participant?.setQuality?.('high')
  }

  function onMeetingJoined() {}
  function onMeetingLeft() {
    setSelectedMic({ id: null, label: null })
    setSelectedWebcam({ id: null, label: null })
    setSelectedSpeaker({ id: null, label: null })
    onMeetingLeave()
  }

  const _handleOnError = ({ code, message }: { code: string; message: string }) => {
    const codeNum = Number.parseInt(code, 10) || 0
    const joiningErrCodes = new Set([
      '4001',
      '4002',
      '4003',
      '4004',
      '4005',
      '4006',
      '4007',
      '4008',
      '4009',
      '4010',
    ])
    const isJoiningError = joiningErrCodes.has(code)
    const isCriticalError = code.startsWith('500') || (codeNum >= 5000 && codeNum < 6000)

    new Audio(
      isCriticalError
        ? 'https://static.videosdk.live/prebuilt/notification_critical_err.mp3'
        : 'https://static.videosdk.live/prebuilt/notification_err.mp3',
    ).play()

    setMeetingErrorVisible(true)
    setMeetingError({
      code,
      message: isJoiningError ? 'Unable to join meeting!' : message,
    })
  }

  const mMeeting = useMeeting({
    onParticipantJoined,
    onMeetingJoined,
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
  })

  useEffect(() => {
    mMeetingRef.current = mMeeting
  }, [mMeeting])

  // PubSub handlers (toast + raise hand)
  usePubSub('RAISE_HAND', {
    onMessageReceived: (data: any) => {
      const localParticipantId = mMeeting?.localParticipant?.id
      const { senderId, senderName } = data || {}
      const isLocal = senderId === localParticipantId

      new Audio('https://static.videosdk.live/prebuilt/notification.mp3').play()

      toast(`${isLocal ? 'You' : nameTruncated(senderName, 15)} raised hand 🖐🏼`, {
        position: 'bottom-left',
        autoClose: 4000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
      })

      if (senderId) participantRaisedHand(senderId)
    },
  })

  usePubSub('CHAT', {
    onMessageReceived: (data: any) => {
      const localParticipantId = mMeeting?.localParticipant?.id
      const { senderId, senderName, message } = data || {}
      const isLocal = senderId === localParticipantId

      if (!isLocal) {
        const notificationAudio = new Audio(
          'https://static.videosdk.live/prebuilt/notification.mp3',
        )
        notificationAudio.volume = 0.1
        notificationAudio.play()

        const label = trimSnackBarText(`${nameTruncated(senderName, 15)} says: ${message ?? ''}`)
        toast(label, {
          position: 'bottom-left',
          autoClose: 4000,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
        })
      }
    },
  })

  return (
    <Layout>
      <div ref={containerRef} className="w-full h-full min-h-0 flex flex-col bg-background">
        <MeetingRoom
          mMeeting={mMeeting}
          containerHeight={containerHeight}
          setIsMeetingLeft={setIsMeetingLeft}
        />

        {meetingErrorVisible && meetingError && (
          <ConfirmBox
            open
            title={`Error Code: ${meetingError.code}`}
            subTitle={meetingError.message}
            successText="OKAY"
            onSuccess={() => {
              setMeetingErrorVisible(false)
              setMeetingError(null)
            }}
            onClose={() => {
              setMeetingErrorVisible(false)
              setMeetingError(null)
            }}
          />
        )}
      </div>
    </Layout>
  )
}

export default MeetingContainer
